import React from 'react';
import { t } from '@citrite/translate';
import {
	AddSquarIcon,
	ArrowLogoutIcon,
	ArrowUndoIcon,
	CloseLightIcon,
	FavoriteActionIcon,
	FavoriteRemoveIcon,
	IInCircleIcon,
	LinkOutIcon,
	NotificationBadgeHalfIcon,
	SettingsWrenchIcon,
	ShowModal,
	ShutDownIcon,
	ShutDownSolidIcon,
	SiriIcon,
	WithModalsProps,
} from '@citrite/web-ui-component';
import { UserAccountData, WorkspaceConfiguration } from '@citrite/workspace-ui-platform';
import { ContextMenuItem } from '@citrite/workspace-ui-platform-react';
import { trackAnalyticsEvent } from 'analytics';
import { trackActionAnalytics } from 'App/Activity/ResourceActionHandler';
import {
	LaunchAction,
	MachineAction,
	SessionAction,
} from 'App/Activity/ResourceManagerContext';
import { ResourceMenuAction } from 'App/Tile/ResourceMenuAction';
import {
	launchResourceWithPrompts,
	LaunchResourceWithPromptsParams,
	shouldShowTroubleshootOption,
} from 'App/Tile/utils';
import { restartDesktop } from 'Components/ResourceTile/restartDesktop';
import { configureSiriForResource } from 'Components/ResourceTile/Siri/configureSiri';
import { ViewAppDetails, ViewDesktopDetails } from 'Components/ResourceTile/ViewDetails';
import { FeatureCanaryConfigAccessor } from 'Configuration';
import { environment } from 'Environment';
import { FeatureFlag } from 'Environment/FeatureFlag';
import { LaunchStatus } from 'Environment/launchResource/launchResource';
import { shouldEnableSiri } from 'Environment/utils';
import { ResourceContext } from 'Workspace/ResourceProvider';
import { Resource, subscriptionStatus } from 'Workspace/ResourceProvider/resourceTypes';
import { AppsUserEventPayload } from 'Workspace/TelemetryEvents/appsEvent/AppsUserEventPayload';
import { DesktopsUserEventPayload } from 'Workspace/TelemetryEvents/desktopsEvent/DesktopsUserEventPayload';
import { EventSite } from 'Workspace/TelemetryEvents/TelemetryEventTypes';

type BuilderProps = {
	resource: Resource;
	resourceContext: ResourceContext;
	workspaceConfiguration: WorkspaceConfiguration;
	hasSubscriptionsEnabled: boolean;
	favoriteRef: React.RefObject<HTMLInputElement>;
	userDetails: UserAccountData;
	canShowBrowserExtensionPrompt: boolean;
	showModal: WithModalsProps['showModal'] | null;
};

const createMenuItem = ({
	icon,
	label,
	onClick,
}: {
	icon: React.ComponentType<{ size?: number }>;
	label: string;
	onClick: () => void;
}): ContextMenuItem => ({
	onClick,
	children: <ResourceMenuAction icon={icon}>{label}</ResourceMenuAction>,
});

const resourceLaunchOption = (params: LaunchResourceWithPromptsParams) =>
	createMenuItem({
		icon: LinkOutIcon,
		label: params.resource.isdesktop
			? t('Workspace:open_desktop')
			: t('Workspace:open_app'),
		onClick: () => launchResourceWithPrompts(params),
	});

const troubleshootOption = (params: LaunchResourceWithPromptsParams) => {
	const handleTroubleshootClick = () => {
		const { resource } = params;
		trackAnalyticsEvent(
			resource.isdesktop
				? DesktopsUserEventPayload.troubleshoot(EventSite.Tile, resource.type)
				: AppsUserEventPayload.troubleshoot(EventSite.Tile, resource.type)
		);
		launchResourceWithPrompts(params);
	};
	return createMenuItem({
		icon: SettingsWrenchIcon,
		label: t('Workspace:inspect_connection.troubleshoot'),
		onClick: handleTroubleshootClick,
	});
};

const restartDesktopOptions = (
	resource: Resource,
	resourceContext: ResourceContext,
	workspaceConfiguration: WorkspaceConfiguration,
	userDetails: UserAccountData,
	canShowBrowserExtensionPrompt: boolean,
	showModal: WithModalsProps['showModal'] | null
) => {
	const reStarting = resourceContext.desktopRestartInProgressIds.includes(resource.id);
	const handleRestartClick = () => {
		if (!reStarting) {
			restartDesktop(resource, { resourceContext }, workspaceConfiguration, {
				userDetails,
				canShowBrowserExtensionPrompt,
				showModal,
			});
			trackActionAnalytics(LaunchAction.RESTART, EventSite.Tile);
		}
	};
	return createMenuItem({
		icon: ArrowUndoIcon,
		label: reStarting
			? t('Workspace:restart_in_progress')
			: t('Workspace:restart_desktop_modal.restart'),
		onClick: handleRestartClick,
	});
};

/**
 * Should extend this method to include more options of desktop resource
 * like logoff, last login, settings etc.
 */
const getMenuItemsForDesktop = (
	resource: Resource,
	resourceContext: ResourceContext,
	workspaceConfiguration: WorkspaceConfiguration,
	userDetails: UserAccountData,
	canShowBrowserExtensionPrompt: boolean,
	showModal: WithModalsProps['showModal'] | null
): ContextMenuItem[] => {
	const menuItem = [];
	const featureCanaryConfig = new FeatureCanaryConfigAccessor(workspaceConfiguration);
	const isGracefulRestartEnabled = featureCanaryConfig.isFeatureEnabled(
		FeatureFlag.EnableGracefulRestart
	);
	if (resource.poweroffurl && !isGracefulRestartEnabled) {
		menuItem.push(
			restartDesktopOptions(
				resource,
				resourceContext,
				workspaceConfiguration,
				userDetails,
				canShowBrowserExtensionPrompt,
				showModal
			)
		);
	}
	return menuItem;
};

const getSubscriptionMenuItem = (
	favoriteRef: React.RefObject<HTMLInputElement>,
	resource: Resource
): ContextMenuItem | false => {
	if (!resource.disabled && !!resource.subscriptionurl) {
		const isSubscribed = resource.subscriptionstatus === subscriptionStatus.subscribed;
		return createMenuItem({
			icon: isSubscribed ? FavoriteRemoveIcon : FavoriteActionIcon,
			label: isSubscribed ? t('Workspace:remove_favorite') : t('Workspace:add_favorite'),
			onClick: () => favoriteRef?.current?.click(),
		});
	}
	return false;
};

const getSiriHandlerForIOS = (resource: Resource, resourceContext: ResourceContext) =>
	createMenuItem({
		icon: SiriIcon,
		label: resource.isResourceRegisteredToSiri
			? t('Workspace:registered_to_siri')
			: t('Workspace:not_registered_to_siri'),
		onClick: () => configureSiriForResource(resource, resourceContext),
	});

const getViewDetailsOptions = (resource: Resource, showModal: ShowModal) =>
	createMenuItem({
		icon: IInCircleIcon,
		label: t('Workspace:view_details'),
		onClick: () => {
			if (resource.isdesktop) {
				trackAnalyticsEvent(
					DesktopsUserEventPayload.moreInfo(EventSite.Tile, resource.type)
				);
				showModal(<ViewDesktopDetails resource={resource} />);
			} else {
				trackAnalyticsEvent(AppsUserEventPayload.moreInfo(EventSite.Tile, resource.type));
				showModal(<ViewAppDetails resource={resource} />);
			}
		},
	});

const getCreateShortcutOption = (resource: Resource) =>
	createMenuItem({
		icon: AddSquarIcon,
		label: t('Workspace:add_to_home_screen'),
		onClick: () => environment.createShortcuts([resource]),
	});

export const moreOptionsBuilder = ({
	resource,
	resourceContext,
	workspaceConfiguration,
	hasSubscriptionsEnabled,
	favoriteRef,
	userDetails,
	canShowBrowserExtensionPrompt,
	showModal,
}: BuilderProps): ContextMenuItem[] => {
	const contextMenu: ContextMenuItem[] = [];

	if (
		shouldShowTroubleshootOption(resource, workspaceConfiguration) &&
		resourceContext?.resourceLatestStatus?.[resource.id]?.status === LaunchStatus.Failed
	) {
		contextMenu.push(
			troubleshootOption({
				resource,
				resourceContext,
				workspaceConfiguration,
				userDetails,
				canShowBrowserExtensionPrompt,
				showModal,
				troubleshoot: true,
			})
		);
	}

	contextMenu.push(
		resourceLaunchOption({
			workspaceConfiguration,
			resource,
			resourceContext,
			userDetails,
			canShowBrowserExtensionPrompt,
			showModal,
		})
	);

	if (resource.isdesktop) {
		contextMenu.push(
			...getMenuItemsForDesktop(
				resource,
				resourceContext,
				workspaceConfiguration,
				userDetails,
				canShowBrowserExtensionPrompt,
				showModal
			)
		);
	}
	if (!resource.mandatory && hasSubscriptionsEnabled) {
		const subscriptionMenuItem = getSubscriptionMenuItem(favoriteRef, resource);
		if (subscriptionMenuItem) {
			contextMenu.push(subscriptionMenuItem);
		}
	}

	if (shouldEnableSiri()) {
		contextMenu.push(getSiriHandlerForIOS(resource, resourceContext));
	}

	if (environment.supportsShortcuts) {
		contextMenu.push(getCreateShortcutOption(resource));
	}

	showModal && contextMenu.push(getViewDetailsOptions(resource, showModal));
	return contextMenu;
};
type DesktopMenuAction =
	| SessionAction
	| LaunchAction.RESTART
	| LaunchAction.RESUME
	| MachineAction;

export const desktopMenuActionsIcons: Record<DesktopMenuAction, React.ComponentType> = {
	[SessionAction.LOGOUT]: ArrowLogoutIcon,
	[SessionAction.DISCONNECT]: CloseLightIcon,
	[LaunchAction.RESTART]: ArrowUndoIcon,
	[LaunchAction.RESUME]: LinkOutIcon,
	[MachineAction.CONTROLLED_RESTART]: ArrowUndoIcon,
	[MachineAction.SHUTDOWN]: ShutDownIcon,
	[MachineAction.POWEROFF]: ShutDownSolidIcon,
	[MachineAction.HIBERNATE]: NotificationBadgeHalfIcon,
};
